/**
 * Fase 2A · P0-3 · ColaboradorOnboarding
 * Blueprint §7.3.2 · 3 pasos: bienvenida · datos personales · servicios propios.
 * Ruta pública (accepta ?token=...) · persistencia localStorage.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingWizard from "../../components/onboarding/OnboardingWizard";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Briefcase, User as UserIcon, CheckCircle, Wrench } from "@phosphor-icons/react";

export default function ColaboradorOnboarding() {
  const navigate = useNavigate();
  const steps = [
    {
      id: "welcome",
      label: "Bienvenida",
      render: ({ data }) => (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
            <Briefcase weight="fill" /> Colaborador
          </div>
          <h2 className="font-display text-2xl font-black tracking-tighter">
            Bienvenido al equipo
          </h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            Has sido invitado a colaborar en un centro AutoGlow. Aquí ofrecerás
            tus servicios bajo el paraguas del centro y verás tu facturación mensual
            de forma transparente.
          </p>
          <ul className="text-sm text-[var(--ag-text)] space-y-2">
            <li className="flex items-start gap-2"><CheckCircle weight="fill" className="text-emerald-400 mt-0.5" size={14} /> Agenda personal filtrada por ti</li>
            <li className="flex items-start gap-2"><CheckCircle weight="fill" className="text-emerald-400 mt-0.5" size={14} /> Servicios propios con precios independientes</li>
            <li className="flex items-start gap-2"><CheckCircle weight="fill" className="text-emerald-400 mt-0.5" size={14} /> Factura auto-generada cada mes</li>
          </ul>
        </div>
      ),
    },
    {
      id: "profile",
      label: "Datos personales",
      render: ({ data, updateData }) => (
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] flex items-center gap-2">
            <UserIcon weight="fill" /> Tu información
          </div>
          <h2 className="font-display text-xl font-bold">Cuéntanos quién eres</h2>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre completo</Label>
            <Input
              value={data.name || ""}
              onChange={(e) => updateData({ name: e.target.value })}
              placeholder="Ej. Miguel Colaborador"
              className="mt-1"
              data-testid="colab-onb-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Teléfono</Label>
            <Input
              value={data.phone || ""}
              onChange={(e) => updateData({ phone: e.target.value })}
              placeholder="600 123 456"
              className="mt-1"
              data-testid="colab-onb-phone"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">NIF (autónomo)</Label>
            <Input
              value={data.nif || ""}
              onChange={(e) => updateData({ nif: e.target.value.toUpperCase() })}
              placeholder="12345678A"
              maxLength={12}
              className="mt-1 font-mono-tab"
              data-testid="colab-onb-nif"
            />
            <div className="text-[11px] text-[var(--ag-text-dim)] mt-1">
              Necesario para generar tu factura mensual. Podrás añadirlo más tarde en tus ajustes.
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "services",
      label: "Tus servicios",
      render: ({ data, updateData }) => (
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] flex items-center gap-2">
            <Wrench weight="fill" /> Servicios propios
          </div>
          <h2 className="font-display text-xl font-bold">¿Qué ofrecerás en el centro?</h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            Anota los servicios principales. Podrás configurar precios y detalles después desde
            <span className="text-[var(--ag-gold)] mx-1">Servicios</span> en tu panel.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Especialidades</Label>
            <Textarea
              value={data.specialties || ""}
              onChange={(e) => updateData({ specialties: e.target.value })}
              placeholder="Detailing premium&#10;Pulido cerámico&#10;Restauración de faros"
              rows={4}
              className="mt-1"
              data-testid="colab-onb-services"
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <OnboardingWizard
      roleKey="colaborador"
      eyebrow="Onboarding · Colaborador"
      title="Configura tu perfil de colaborador"
      subtitle="En 3 pasos rápidos. Podrás editar todo desde tus ajustes."
      steps={steps}
      homeRoute="/login"
      homeCta="Iniciar sesión"
      onComplete={() => navigate("/login")}
      onSkip={() => navigate("/login")}
    />
  );
}
